
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ICategoryPeriod, ISubcategoryPeriod, ICategoryReportedPom, ISubcategoryReportedPom, IPomReportSubCategoryData } from '../util/DataModelInterface'
import { DataTable } from '../components/DataTable';
import { Spinner } from '../components/Spinner';
import useToastHandler from '../hooks/useToastHandler';
import { getReportedPom } from '../api/PomReport';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCategories } from '../util/StorageUtil';
import { formatFinNumber } from '../util/FormatDateUtil';
import * as reportApi from '../api/PomReport';

export const ReportedPom = () => {
  const intl = useIntl();
  const { state } = useLocation();
  const navigate = useNavigate();
  const tableRef = useRef<any>(null);
  const catHeadings = ['name', 'period', 'total_fin_kg', 'total_fin_pcs', 'total_aland_kg', 'total_aland_pcs', 'total_b2b_kg', 'total_b2b_pcs', 'total_kg', 'total_pcs'];
  const subCatHeadings = ['name', 'period', 'unit', 'total_fin', 'total_aland', 'total_b2b', 'total'];

  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([] as any);
  const [unit, setUnit] = useState('');
  const { addToast } = useToastHandler();

  useEffect(() => {
    setLoading(true);
    fetchUnit().finally(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getReportedPom(state.periodStart, state.periodEnd, state.category).then((data: any) => {
      if (data?.error) {
        addToast(data.error, 'reportedPom', 'fetch');
      } else if (data) {
        setTableData(parseTableData(data));
      }
    }).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  const fetchUnit = async () => {
    if(!isNaN(state.category)) {
      let categories = await getCategories();
      categories?.every((c) => {
        let sub = c.subs?.find((s) => { return s.code === state.category; }) as IPomReportSubCategoryData;
        if(sub) {
          setUnit(sub?.unit ? sub.unit : '');
          return false;
        }
        return true;
      });
    }
  }

  const parseTableData = (reported: any[]) => { 
    if(isNaN(state.category)) {
      let data = [] as ICategoryReportedPom[];
      if(reported && Array.isArray(reported)) {
        let i = 0;
        reported.forEach((r) => {
          let subRows = [] as ICategoryPeriod[];
          r.periods.forEach((p: ICategoryPeriod) => {
            let monthStr = p.period.split("-")[1];
            let yearStr = p.period.split("-")[0];
            let reportingPeriod = monthStr + "/" + yearStr;
            subRows.push({
              name: '',
              company: r.company,
              period: reportingPeriod,
              total_fin_kg: formatFinNumber(p.total_fin_kg),
              total_fin_pcs: p.total_fin_pcs,
              total_aland_kg: formatFinNumber(p.total_aland_kg),
              total_aland_pcs: p.total_aland_pcs,
              total_b2b_kg: formatFinNumber(p.total_b2b_kg),
              total_b2b_pcs: p.total_b2b_pcs,
              total_kg: formatFinNumber(p.total_kg),
              total_pcs: p.total_pcs
            });
          });
          // Group heading row
          data[i] = {
            name: r.name,
            company: r.company,
            subRows: subRows
          };
          i++;
        });
        return data;
      }
    } else {
      let data = [] as ISubcategoryReportedPom[];
      if(reported && Array.isArray(reported)) {
        let i = 0;
        reported.forEach((r) => {
          let subRows = [] as ISubcategoryPeriod[];
          r.periods.forEach((p: ISubcategoryPeriod) => {
            let monthStr = p.period.split("-")[1];
            let yearStr = p.period.split("-")[0];
            let reportingPeriod = monthStr + "/" + yearStr;
            subRows.push({
              name: '',
              unit: unit,
              period: reportingPeriod,
              total_fin: unit === 'kg' ? formatFinNumber(p.total_fin) : p.total_fin,
              total_aland: unit === 'kg' ? formatFinNumber(p.total_aland) : p.total_aland,
              total_b2b: unit === 'kg' ? formatFinNumber(p.total_b2b) : p.total_b2b,
              total: unit === 'kg' ? formatFinNumber(p.total) : p.total,
              company: p.company
            });
          });
          // Group heading row
          data[i] = {
            name: r.name,
            company: r.company,
            subRows: subRows
          };
          i++;
        });
        return data;
      }
    }
  }

  const exportReport = async () => {
    let reportJsonData = [] as any;
    if(isNaN(state.category)) {
      let sum_fin_kg = 0;
      let sum_fin_pcs = 0;
      let sum_aland_kg = 0;
      let sum_aland_pcs = 0;
      let sum_b2b_kg = 0;
      let sum_b2b_pcs = 0;
      let sum_kg = 0;
      let sum_pcs = 0;
      tableData?.forEach((r: any) => {
        // Group heading row
        reportJsonData.push({
          [intl.formatMessage({id: 'reportedpom.name'})]: r.name,
          'Id': r.company,
          [intl.formatMessage({id: 'reportedpom.period'})]: '',
          [intl.formatMessage({id: 'reportedpom.total_fin_kg'})]: '',
          [intl.formatMessage({id: 'reportedpom.total_fin_pcs'})]: '',
          [intl.formatMessage({id: 'reportedpom.total_aland_kg'})]: '',
          [intl.formatMessage({id: 'reportedpom.total_aland_pcs'})]: '',
          [intl.formatMessage({id: 'reportedpom.total_b2b_kg'})]: '',
          [intl.formatMessage({id: 'reportedpom.total_b2b_pcs'})]: '',
          [intl.formatMessage({id: 'reportedpom.total_kg'})]: '',
          [intl.formatMessage({id: 'reportedpom.total_pcs'})]: ''});
        r.subRows.forEach((p: ICategoryPeriod) => {
          sum_fin_kg += parseFloat(p.total_fin_kg);
          sum_fin_pcs += parseFloat(p.total_fin_pcs);
          sum_aland_kg += parseFloat(p.total_aland_kg);
          sum_aland_pcs += parseFloat(p.total_aland_pcs);
          sum_b2b_kg += parseFloat(p.total_b2b_kg);
          sum_b2b_pcs += parseFloat(p.total_b2b_pcs);
          sum_kg += parseFloat(p.total_kg);
          sum_pcs += parseFloat(p.total_pcs);
          reportJsonData.push({
            [intl.formatMessage({id: 'reportedpom.name'})]: ' ',
            'Id': '',
            [intl.formatMessage({id: 'reportedpom.period'})]: p.period,
            [intl.formatMessage({id: 'reportedpom.total_fin_kg'})]: parseFloat(p.total_fin_kg),
            [intl.formatMessage({id: 'reportedpom.total_fin_pcs'})]: parseFloat(p.total_fin_pcs),
            [intl.formatMessage({id: 'reportedpom.total_aland_kg'})]: parseFloat(p.total_aland_kg),
            [intl.formatMessage({id: 'reportedpom.total_aland_pcs'})]: parseFloat(p.total_aland_pcs),
            [intl.formatMessage({id: 'reportedpom.total_b2b_kg'})]: parseFloat(p.total_b2b_kg),
            [intl.formatMessage({id: 'reportedpom.total_b2b_pcs'})]: parseFloat(p.total_b2b_pcs),
            [intl.formatMessage({id: 'reportedpom.total_kg'})]: parseFloat(p.total_kg),
            [intl.formatMessage({id: 'reportedpom.total_pcs'})]: parseFloat(p.total_pcs)});
        });
      });
      // Summary row
      reportJsonData.push({
        [intl.formatMessage({id: 'reportedpom.name'})]: '',
        'Id': '',
        [intl.formatMessage({id: 'reportedpom.period'})]: intl.formatMessage({id: 'reportedpom.total'}),
        [intl.formatMessage({id: 'reportedpom.total_fin_kg'})]: sum_fin_kg,
        [intl.formatMessage({id: 'reportedpom.total_fin_pcs'})]: sum_fin_pcs,
        [intl.formatMessage({id: 'reportedpom.total_aland_kg'})]: sum_aland_kg,
        [intl.formatMessage({id: 'reportedpom.total_aland_pcs'})]: sum_aland_pcs,
        [intl.formatMessage({id: 'reportedpom.total_b2b_kg'})]: sum_b2b_kg,
        [intl.formatMessage({id: 'reportedpom.total_b2b_pcs'})]: sum_b2b_pcs,
        [intl.formatMessage({id: 'reportedpom.total_kg'})]: sum_kg,
        [intl.formatMessage({id: 'reportedpom.total_pcs'})]: sum_pcs});
    } else {
      let sum_fin = 0;
      let sum_aland = 0;
      let sum_b2b = 0;
      let sum = 0;
      tableData?.forEach((r: any) => {
        reportJsonData.push({
          [intl.formatMessage({id: 'reportedpom.name'})] : r.name,
          'Id': r.company,
          [intl.formatMessage({id: 'reportedpom.period'})]: '',
          [intl.formatMessage({id: 'reportedpom.unit'})]: '',
          [intl.formatMessage({id: 'reportedpom.total_fin'})]: '',
          [intl.formatMessage({id: 'reportedpom.total_aland'})]: '',
          [intl.formatMessage({id: 'reportedpom.total_b2b'})]: '',
          [intl.formatMessage({id: 'reportedpom.total'})]: ''});
        r.subRows.forEach((p: ISubcategoryPeriod) => {
          sum_fin += parseFloat(p.total_fin);
          sum_aland += parseFloat(p.total_aland);
          sum_b2b += parseFloat(p.total_b2b);
          sum += parseFloat(p.total);
          reportJsonData.push({
            [intl.formatMessage({id: 'reportedpom.name'})]: ' ',
            'Id': '',
            [intl.formatMessage({id: 'reportedpom.period'})]: p.period,
            [intl.formatMessage({id: 'reportedpom.unit'})]: '',
            [intl.formatMessage({id: 'reportedpom.total_fin'})]: parseFloat(p.total_fin),
            [intl.formatMessage({id: 'reportedpom.total_aland'})]: parseFloat(p.total_aland),
            [intl.formatMessage({id: 'reportedpom.total_b2b'})]: parseFloat(p.total_b2b),
            [intl.formatMessage({id: 'reportedpom.total'})]: parseFloat(p.total)
          });
        });
      });
      // Summary row
      reportJsonData.push({
        [intl.formatMessage({id: 'reportedpom.name'})]: '',
        'Id': '',
        [intl.formatMessage({id: 'reportedpom.period'})]: intl.formatMessage({id: 'reportedpom.total'}),
        [intl.formatMessage({id: 'reportedpom.unit'})]: unit,
        [intl.formatMessage({id: 'reportedpom.total_fin'})]: sum_fin,
        [intl.formatMessage({id: 'reportedpom.total_aland'})]: sum_aland,
        [intl.formatMessage({id: 'reportedpom.total_b2b'})]: sum_b2b,
        [intl.formatMessage({id: 'reportedpom.total'})]: sum});
    }
    await reportApi.exportReport(
    { header: state.periodStart + "-" + state.periodEnd + '_' + state.category,
      data: reportJsonData
    })
  }

  const toolBar = () => {
    return(
    <div className="toolbar">
      <span className="item-actions ms-auto">
        {
        <button key='toolbarbuttonsend' className="btn btn-primary btn-send"
          onClick={async () => { await exportReport(); }}>
          <i className="fa fa-download me-2" aria-hidden="true"></i>
          <FormattedMessage id={'button.reportedpom.download'}/>
        </button>}
      </span>
    </div>);
  }

  return (
    <>
      <Spinner active={loading} />
      <div>
        <div className="action-buttons d-flex flex-row align-items-start">
          <div className="page-header">
            <span className="info ms-1">{state.periodStart.replace('-','/')} - {state.periodEnd.replace('-','/')}</span>
            <h1> {state.category} {state.name}</h1>
            <p className="info"><i className="fa fa-info-circle me-2"></i><FormattedMessage id="reportedpom.statusinfo"/></p>
          </div>
          <div className="ms-auto d-flex flex-row align-items-center">
            <button type="button" className="btn btn-secondary" onClick={() => { navigate(-1); }}>
              <i className="fa fa-close me-2"></i>
              <FormattedMessage id="button.close"/>
            </button>
          </div>
        </div>
        <DataTable  ref={tableRef}
                    tableheadings={isNaN(state.category) ? catHeadings : subCatHeadings}
                    tabledata={tableData} caption={'reportedpom.tablecaption'}
                    tableType={'reportedpom'} tableId={'reportedpom'} 
                    toolbar={toolBar()}
                    hiddenColumns={['company']}
                    />
      </div>
    </>
  );
};